import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal, ModalContent, ModalOverlay, rem, SearchAltOutline, useBreakpointValue, useDisclosure } from '@bonusly/design-system';

import { SearchContent } from '../../../components/search';
import { useKeyboardTrigger } from '../../../hooks/use-keyboard-trigger';
import { AnalyticsEvent } from '../../../lib/analytics/events';
import { useAnalytics } from '../../../lib/analytics/use-analytics';
import { isMacOS } from '../../top-navbar/utils/agent';
import { IconAndTextContent } from './icon-and-text-content';
import { UserNavAction } from './user-nav-action';

interface SearchNavItemProps {
	isExpanded: boolean;
}

export const SearchNavItem: FC<SearchNavItemProps> = ({ isExpanded }) => {
	const { t } = useTranslation('navigation');
	const { track } = useAnalytics();
	const { isOpen, onClose, onOpen } = useDisclosure();
	const isAtLeastXs = useBreakpointValue({ base: false, xs: true });

	useKeyboardTrigger('k', onOpen, { meta: true });

	// Determine keyboard shortcut based on platform
	const shortcut = isMacOS() ? '⌘K' : 'Ctrl+K';
	const tooltipLabel = `${t('links.search')} (${shortcut})`;

	return (
		<>
			<UserNavAction
				onClick={() => {
					track(AnalyticsEvent.UserNavbar.SearchNavigation);
					onOpen();
				}}
				data-testid="nav-link-search"
				label={tooltipLabel}
				isExpanded={isExpanded}
			>
				<IconAndTextContent icon={<SearchAltOutline size="md" />} label={t('links.search')} isExpanded={isExpanded} shortcut={shortcut} />
			</UserNavAction>

			<Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="2xl">
				<ModalOverlay />
				<ModalContent
					bg="bg.surface"
					maxH={`min(calc(100% - 7.5rem), ${rem(664)})`}
					{...(!isAtLeastXs && {
						height: 'auto',
						maxHeight: '100%',
						borderRadius: 0,
						maxH: 'auto',
						my: 0,
					})}
				>
					<SearchContent onClose={onClose} />
				</ModalContent>
			</Modal>
		</>
	);
};
